/* eslint-disable react/jsx-props-no-spreading */
import { ANALYTICS_TYPE, LAYER_PLACEMENT, VIEW } from '../../enums';
import { AcceptAllButton, DenyAllButton, MoreButton, SaveButton } from '../Buttons';
import { Fragment, FunctionalComponent, h } from 'preact';
import { HorizontalContainer, VerticalContainer } from '../../components/MainLayout/MainLayout.styles';
import Usercentrics, {
  DefaultUISettings,
  SECOND_LAYER_TRIGGER,
  TRACKING_EVENT_TYPE,
} from '@usercentrics/cmp-browser-sdk';
import { dispatchAnalyticsEvent, mapUIVariantToLayer, setTrackingPixel } from '../../modules/helpers';
import { useDeviceState, useMutation } from '../../hooks';
import CategoryToggle from '../../components/Toggles/CategoryToggle/CategoryToggle';
import { DefaultButtons } from '../../components/ButtonsContainer/interface';
import DefaultTabs from './DefaultTabs';
import MainLayout from '../../components/MainLayout/MainLayout';
import MapCategories from '../../components/CategoryHandler/MapCategories';
import { Tabs } from '../../components/Tabs';
import { getUserDecision } from '../../App.helpers';
import { memo } from 'preact/compat';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { useEffect } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useTheme } from 'styled-components';
import { useUiContext } from '../../contexts/UiContext';

const testIds = {
  firstLayerBanner: 'uc-default-banner',
  firstLayerWall: 'uc-default-wall',
  secondLayer: 'uc-default-settings',
};

interface Props {
  onClose: () => void;
  onCustomize: () => Promise<void>;
  onFullScreenAmp: () => void;
  onLanguageChange: (selectedLanguage: string) => void;
  onAcceptAll: () => Promise<void>;
  onDenyAll: () => Promise<void>;
  isAcceptDenyAllLoading: boolean;
  UC: Usercentrics;
}

const Default: FunctionalComponent<Props> = ({
  onClose,
  onCustomize,
  onFullScreenAmp,
  onLanguageChange,
  onAcceptAll,
  onDenyAll,
  isAcceptDenyAllLoading,
  UC,
}) => {
  const { settings } = useSettingsContext();
  const { isIE, isMobile } = useDeviceState();
  const { categories, getUpdatedCategories, setCategories } = useCategoryContext();
  const {
    abTestVariant,
    closeView,
    firstLayerVariant,
    isAmpEnabled,
    isInFullScreen,
    secondLayerVariant,
    view,
    setFirstLayerVariant,
    setSecondLayerVariant,
  } = useUiContext();
  const layerConfig = mapUIVariantToLayer(view === VIEW.FIRST_LAYER ? firstLayerVariant : secondLayerVariant, testIds);

  const theme = useTheme();
  const ui = settings.ui as DefaultUISettings;
  const [dismissAmp, { loading: dismissAmpLoading }] = useMutation(UC.dismissAmp, UC);
  const [updateServices, { loading: updateServicesLoading }] = useMutation(UC.updateServices, UC);

  useEffect(() => {
    const defaultSettings = settings.ui as DefaultUISettings;
    // We only have these UI variations when TCF is disabled. For TCF we only have a single variant for each layer.
    setFirstLayerVariant(defaultSettings.firstLayer.variant);
    setSecondLayerVariant(defaultSettings.secondLayer.variant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This method should always be triggered when clicking on the AcceptAll button of Default UI.
   */
  const onAcceptAllHandler = (): void => {
    onAcceptAll().then(() => {
      closeView();
    });
  };

  /**
   * This method should always be triggered when clicking on the DenyAll button of Default UI.
   */

  const onDenyAllHandler = (): void => {
    onDenyAll().then(() => {
      closeView();
    });
  };

  /**
   * This method should always be triggered when clicking on the Save button of Default UI.
   */
  const onSaveHandler = (): void => {
    if (updateServicesLoading || dismissAmpLoading) {
      return;
    }

    updateServices(getUserDecision(categories)).then(async () => {
      if (isAmpEnabled) {
        await dismissAmp();
      }
      dispatchAnalyticsEvent(view, ANALYTICS_TYPE.SAVE, abTestVariant);
      setTrackingPixel(TRACKING_EVENT_TYPE.CUSTOM_CHOICE, UC.setTrackingPixel, settings.interactionAnalyticsEnabled);
      setCategories(await getUpdatedCategories(view));
      closeView();
    });
  };

  const loadingMutation = updateServicesLoading || dismissAmpLoading || isAcceptDenyAllLoading;

  const acceptAll = <AcceptAllButton disabled={loadingMutation} onClick={onAcceptAllHandler} />;
  const denyAll = <DenyAllButton disabled={loadingMutation} onClick={onDenyAllHandler} />;
  const save = <SaveButton disabled={loadingMutation} onClick={onSaveHandler} />;
  const more = <MoreButton disabled={loadingMutation} onClick={onCustomize} />;

  const buttonsToShowInFirstLayer: DefaultButtons = {
    AcceptAllButton: acceptAll,
    ...(!ui.firstLayer.hideButtonDeny && { DenyAllButton: denyAll }),
    ...(ui.firstLayer.isCategoryTogglesEnabled && { SaveButton: save }),
    ...(theme.ui.firstLayer.secondLayerTrigger === SECOND_LAYER_TRIGGER.BUTTON && { MoreButton: more }),
  };

  let buttonsToShowInSecondLayer: DefaultButtons = { AcceptAllButton: acceptAll };

  if (isAmpEnabled) {
    buttonsToShowInSecondLayer = { ...buttonsToShowInSecondLayer, DenyAllButton: denyAll };
  } else if (ui.secondLayer.hideButtonDeny) {
    buttonsToShowInSecondLayer = { ...buttonsToShowInSecondLayer, SaveButton: save };
  } else {
    buttonsToShowInSecondLayer = { ...buttonsToShowInSecondLayer, DenyAllButton: denyAll, SaveButton: save };
  }

  const headerFunctions = {
    onClose,
    onFullScreenAmp,
    onLanguageChange,
  };

  const isBanner =
    layerConfig.layerPlacement === LAYER_PLACEMENT.BOTTOM || layerConfig.layerPlacement === LAYER_PLACEMENT.TOP;

  const firstLayerCustomActions =
    theme.ui.firstLayer.secondLayerTrigger === SECOND_LAYER_TRIGGER.LINK
      ? [{ ariaLabel: ui.buttons.showSecondLayer.label, label: ui.buttons.showSecondLayer.label, onClick: onCustomize }]
      : [];
  /**
   * SWITCH VIEW - Decision logic for showing the correct view and variant.
   */
  switch (view) {
    case VIEW.FIRST_LAYER:
      return (
        <MainLayout
          customActions={firstLayerCustomActions}
          footer={{
            buttons: buttonsToShowInFirstLayer,
          }}
          header={{
            description: ui.firstLayer.description.default,
            enableHTMLTextFormatting: true,
            functions: headerFunctions,
            labelShowLess: ui.labels.general.showLess,
            labelShowMore: ui.labels.general.showMore,
            shortDescription: ui.firstLayer.description.short,
            showFullScreenButton: isAmpEnabled && !isInFullScreen,
            showLanguageSelector: ui.firstLayer.isLanguageSelectorEnabled,
            showMoreEnabled: ui.firstLayer.description.showShortDescriptionOnMobile,
            title: ui.firstLayer.title,
          }}
          layerSettings={{
            isOverlayEnabled: ui.firstLayer.isOverlayEnabled,
            ...((isIE || isMobile) && { minVerticalScrollerHeight: 100 }),
            ...layerConfig,
            placeLinksInFooter: isBanner,
            showBorderBottom: false,
            withFadingBottom: false,
            withInitialHeight: true,
          }}
          useBackgroundColor
        >
          {ui.firstLayer.isCategoryTogglesEnabled && (
            <VerticalContainer>
              <HorizontalContainer>
                {MapCategories({ categories, setCategories }).map((categoryProps) => (
                  <CategoryToggle {...categoryProps} />
                ))}
              </HorizontalContainer>
            </VerticalContainer>
          )}
        </MainLayout>
      );

    case VIEW.SECOND_LAYER:
      return (
        <Tabs>
          <MainLayout
            footer={{
              buttons: buttonsToShowInSecondLayer,
              showBorderBeforeFooter: true,
            }}
            header={{
              description: ui.secondLayer.description,
              enableHTMLTextFormatting: true,
              functions: headerFunctions,
              showCloseButton: (isAmpEnabled && isInFullScreen) || !isAmpEnabled,
              showFullScreenButton: isAmpEnabled && !isInFullScreen,
              showLanguageSelector: ui.secondLayer.isLanguageSelectorEnabled,
              title: ui.secondLayer.title,
            }}
            layerSettings={{
              isOverlayEnabled: ui.secondLayer.isOverlayEnabled,
              ...(isIE && { minVerticalScrollerHeight: 350 }),
              ...layerConfig,
              placeLinksInFooter: isBanner,
              showBorderBottom: false,
              side: ui.secondLayer.side,
              withFadingBottom: true,
            }}
            useBackgroundColor={false}
          >
            <DefaultTabs />
          </MainLayout>
        </Tabs>
      );
    default:
      return <Fragment />;
  }
};

export default memo(Default);
